/**
 * Custom Turbo Stream Actions
 *
 * Extends Turbo Stream with custom actions for enhanced navigation.
 * These actions allow turbo_stream responses to perform additional
 * browser operations like URL updates.
 */
import * as Turbo from '@hotwired/turbo'

/**
 * Enhanced error handling for turbo-frame-missing events.
 * When a turbo-frame requests content but doesn't find a matching frame
 * in the response, this handler provides detailed debugging information
 * instead of just showing "Content missing".
 */
document.addEventListener('turbo:frame-missing', (event) => {
  const { target, detail } = event
  const frameId = target.id || '(no id)'
  const frameSrc = target.src || target.getAttribute('src') || '(no src)'
  const responseUrl = detail.response?.url || '(unknown URL)'

  console.error(
    `[Turbo] Frame content missing:\n` +
    `  Frame ID: "${frameId}"\n` +
    `  Frame src: ${frameSrc}\n` +
    `  Response URL: ${responseUrl}\n` +
    `  Expected: <turbo-frame id="${frameId}"> in the response\n` +
    `  Tip: Ensure the response contains a matching turbo-frame element, ` +
    `or use data-turbo-frame="_top" to replace the whole page.`
  )

  // In development, replace the generic "Content missing" with a helpful message
  if (process.env.NODE_ENV !== 'production') {
    event.preventDefault()
    target.innerHTML = `
      <div style="padding: 1rem; background: #fee; border: 1px solid #c00; border-radius: 4px; margin: 1rem 0;">
        <strong style="color: #c00;">Turbo Frame Missing</strong>
        <p style="margin: 0.5rem 0 0; font-size: 0.9rem;">
          Frame <code>"${frameId}"</code> expected matching content from:<br>
          <code>${responseUrl}</code>
        </p>
        <p style="margin: 0.5rem 0 0; font-size: 0.85rem; color: #666;">
          Check the console for more details.
        </p>
      </div>
    `
  }
})

/**
 * Updates the browser URL without triggering a page navigation.
 * Use in turbo_stream responses to sync URL with content changes.
 *
 * Usage in ERB:
 *   <%= turbo_stream.action(:update_url, profile_admin_tools_projects_path) %>
 *
 * Rendered as:
 *   <turbo-stream action="update_url" url="/profile/admin-tools/projects"></turbo-stream>
 */
Turbo.StreamActions.update_url = function () {
  const url = this.getAttribute('url')
  if (url) {
    history.pushState({ turbo: true }, '', url)
  }
}
